import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import {
  buildHtml,
  DonationRoute,
  RouteInput,
  RouteSlotInput,
} from '@caritas/ui';
import { graphql, useStaticQuery } from 'gatsby';

import { PurposeDonationsQuery } from '../../generated/types/gatsby';
import { useQueryParamString } from '../commons/query-string';
import { languageFallback } from '../utils/localizations';
import {
  hasImage,
} from './blocks/ContentPageHeader.mappers';
import { mapImage } from './Image.mappers';

export type WidgetIds = {
  campaignId: string;
  campaignSubId: string;
  sextantProductUid: string;
  sextantContractTemplateUid: string;
};

const useDonationPurposeTexts = (donationType?: string) => {
  const allDonations = useStaticQuery<PurposeDonationsQuery>(graphql`
    query PurposeDonations {
      allDrupalDonation {
        nodes {
          drupalId
          translations {
            title
            langcode
          }
        }
      }
      allDrupalSponsorshipDonation {
        nodes {
          drupalId
          translations {
            title
            langcode
          }
        }
      }
    }
  `);
  const translatedPurposeTexts: {
    [langcode: string]: {
      [drupalId: string]: string;
    };
  } = {};

  const donations = {
    Donation: allDonations.allDrupalDonation,
    SponsorshipDonation: allDonations.allDrupalSponsorshipDonation,
  };
  const donationTypes: Array<keyof typeof donations> = [
    'Donation',
    'SponsorshipDonation',
  ];

  donationTypes.forEach((donationTypeItem) => {
    if (!donationType || donationType === donationTypeItem) {
      donations[donationTypeItem].nodes.length > 0 &&
        donations[donationTypeItem].nodes.map((item) => {
          item.translations.map((translatedItem) => {
            if (!translatedPurposeTexts[translatedItem.langcode]) {
              translatedPurposeTexts[translatedItem.langcode] = {};
            }
            if (
              !translatedPurposeTexts[translatedItem.langcode]![item.drupalId]
            ) {
              translatedPurposeTexts[translatedItem.langcode]![item.drupalId] =
                translatedItem.title;
            }
          });
        });
    }
  });

  return translatedPurposeTexts;
};

export function mapDonationBox(
  input: BoxDonationPageFragment | undefined,
  pageContext: SilverbackPageContext,
): RouteSlotInput<typeof DonationRoute, 'body', 'donationBox'> {
  return {
    key: 'donationBox',
    input: function useMapDonationBox() {
      const [rnwWiew] = useQueryParamString('rnw-view', '');
      const [dds_subscription] = useQueryParamString('dds-subscription', '');
      const [subscription_token] = useQueryParamString(
        'subscription_token',
        '',
      );
      const translatedPurposeTexts = useDonationPurposeTexts();
      const pageLanguage = languageFallback(pageContext.locale || 'de');
      const donationPurposeLabel = input?.donationPurpose?.translations.reduce(
        (accumulator, currentValue) => {
          if (currentValue.langcode === pageLanguage) {
            accumulator = currentValue.title;
          }
          return accumulator;
        },
        input?.donationPurpose.title,
      );
      const thankYouPagePath =
        input?.donationPurpose?.thankYouPage?.translations.reduce(
          (accumulator, currentValue) => {
            if (currentValue.langcode === pageLanguage) {
              accumulator = currentValue.path;
            }
            return accumulator;
          },
          input?.donationPurpose?.thankYouPage.path,
        );
      const boxLabels = input?.translations.reduce(
        (accumulator, currentValue) => {
          if (currentValue.langcode === pageLanguage) {
            accumulator = {
              labelCustomAmountDescription: currentValue.labelCustomAmountDescription,
              labelCustomAmountButton: currentValue.labelCustomAmountButton,
              labelCustomAmount: currentValue.labelCustomAmount,
              labelBoxContentTitle: currentValue.labelBoxContentTitle,
              labelBoxContentValue: currentValue.labelBoxContentValue,
              labelCloseBoxButton: currentValue.labelCloseBoxButton,
              labelOpenBoxButton: currentValue.labelOpenBoxButton,
              labelFooterText: currentValue.labelFooterText
            }
          }
          return accumulator;
        },
        {},
      );

      const boxGraphics = input?.translations.reduce(
        (accumulator, currentValue) => {
          if (currentValue.langcode === pageLanguage) {
            accumulator = {
              openBoxImage: currentValue.openBoxImage,
              closedBoxImage: currentValue.closedBoxImage,
            }
          }
          return accumulator;
        },
        {},
      );

      const donationItems = input?.items.map((item) => {
        return {
          amount: item.amount,
          donationIcon: item.donationIcon?.translations.reduce((accumulator, currentValue) => {
            if (currentValue.langcode === pageLanguage) {
              accumulator = {
                ...accumulator,
                label: currentValue.label,
                svgSource: currentValue.svgSource
              }
            }
            return accumulator;
          },
          {
            label: item.donationIcon.label,
            originalLabel: item.donationIcon.label,
            svgSource: item.donationIcon.svgSource
          }),
        }
      }) || [];

      return [
        {
          items: donationItems,
          customItem: Boolean(input?.customOption),
          boxLabels: boxLabels,
          boxGraphics: boxGraphics,
          donationForm: {
            nodeId: input?.donationPurpose?.drupalId || '1',
            donationPurposeLabel,
            language: pageLanguage,
            ...((typeof dds_subscription === 'undefined' ||
              dds_subscription === '') &&
            (typeof subscription_token === 'undefined' ||
              subscription_token === '')
              ? {
                  campaignId: input?.donationPurpose?.campaignId || '',
                  campaignSubId: input?.donationPurpose?.campaignSubId || '',
                }
              : {}),
            sextantProductId: input?.donationPurpose?.sextantProductUid || '',
            sextantContractTemplateUid:
              input?.donationPurpose?.sextantContractTemplateUid || '',
            purposeTexts: translatedPurposeTexts,
            thankYouPagePath: thankYouPagePath,
            debug: false,
            widget: 'donation',
            isCustomAmount: true,
            amount: 0,
            period: 'oneTime',
            testMode: !process.env.GATSBY_RAISENOW_PRODUCTION,
          },
          // In case we have the rnw-view parameter set, then we want to actually
          // show the form, which will basically contain the thank you / error
          // message.
          showForm:
            typeof rnwWiew !== 'undefined' && rnwWiew !== '' ? true : false,
        },
        200,
      ];
    },
  };
}

export function mapDonationPageHeader(input: {
  headline: string;
  lead: string;
}): RouteSlotInput<typeof DonationRoute, 'body', 'formattedText'> {
  return {
    key: 'formattedText',
    input: {
      Content: buildHtml(
        `
          <h1 class="pt-6 font-bold leading-tight lg:leading-tight text-3xl lg:text-4xl tracking-[-0.01em]">${input.headline}</h1>
          ${input.lead ? `<p>${input.lead}</p>` : ``}
  `,
      ),
    },
  };
}

export function mapDonationPageDisclaimer(input: {
  disclaimer: string;
}): RouteSlotInput<typeof DonationRoute, 'body', 'formattedText'> {
  return {
    key: 'formattedText',
    input: {
      Content: buildHtml(
        `${input.disclaimer ? `<p>${input.disclaimer}</p>` : ``}`,
      ),
    },
  };
}

export const mapDonationBoxHeader = (
  input: BoxDonationPageFragment | undefined,
): RouteSlotInput<typeof DonationRoute, 'body', 'heroSection'> => {
  const image = hasImage(input?.headerImage) ? input?.headerImage.image : undefined;

  return {
    key: 'heroSection',
    input: () => [
      {
        title: input?.callToAction || '',
        description: input?.lead || '',
        Image:
          image &&
          mapImage({
            image,
            preset: 'header_landscape',
          }),
        MobileImage:
          image &&
          mapImage({
            image,
            preset: 'header_portrait',
            sizes: '',
          }),
      },
      200,
    ],
  };
};

export function mapDonationForm(
  input:
    | DonationPageFragment
    | StockDonationPageFragment
    | SponsorshipDonationPageFragment
    | undefined,
  pageContext: SilverbackPageContext,
  isDonationBox?: boolean,
): RouteSlotInput<typeof DonationRoute, 'body', 'DonationForm'> {
  return {
    key: 'DonationForm',
    input: function useDonationForm() {
      const [amount] = useQueryParamString('amount', '');
      const [custom] = useQueryParamString('custom', '');
      const [dds_subscription] = useQueryParamString('dds-subscription', '');
      const [subscription_token] = useQueryParamString(
        'subscription_token',
        '',
      );
      const [frequency] = useQueryParamString(
        'frequency',
        input?.__typename === 'SponsorshipDonation' ? 'monthly' : 'oneTime',
      );
      const [description] = useQueryParamString('description', '');
      const amountKeys = ['oneTime', 'monthly', 'yearly'];
      const amounts = amountKeys.reduce((accumulator, amountKey) => {
        return {
          ...accumulator,
          // @ts-ignore
          ...(input[`${amountKey}Amounts`]
            ? {
                [amountKey]: {
                  // @ts-ignore
                  amounts: (input[`${amountKey}Amounts`].amounts || [])
                    .filter((amount: string) => amount !== '*')
                    .map((amount: string) => parseInt(amount)),
                  // @ts-ignore
                  default: input[`${amountKey}Amounts`].default,
                },
              }
            : []),
        };
      }, {});
      const translatedPurposeTexts = useDonationPurposeTexts();
      const pageLanguage = languageFallback(pageContext.locale || 'de');
      const thankYouPagePath = input?.thankYouPage?.translations.reduce(
        (accumulator, currentValue) => {
          if (currentValue.langcode === pageLanguage) {
            accumulator = currentValue.path;
          }
          return accumulator;
        },
        input?.thankYouPage.path,
      );

      return [
        {
          nodeId: input?.drupalId || '1',
          donationPurposeLabel: input?.title,
          language: pageLanguage,
          // We add the campaing id and subid only if the dds_subscription and subscription_token is
          // not present in the URL query. Otherwise, those values will be
          // predefined in the widget.
          ...((typeof dds_subscription === 'undefined' ||
            dds_subscription === '') &&
          (typeof subscription_token === 'undefined' ||
            subscription_token === '')
            ? {
                campaignId: input?.campaignId || '',
                campaignSubId: input?.campaignSubId || '',
              }
            : {}),
          purposeTexts: translatedPurposeTexts,
          sextantProductId: input?.sextantProductUid || '',
          sextantContractTemplateUid: input?.sextantContractTemplateUid || '',
          debug: false,
          // TODO: Switch types? Different templates?
          // type: 'donation',
          widget:
            input?.__typename === 'Donation'
              ? 'donation'
              : input?.__typename === 'StockDonation'
              ? 'climate'
              : input?.__typename === 'SponsorshipDonation'
              ? 'sponsor'
              : 'donation',
          amounts: isDonationBox ? {} : amounts,
          isCustomAmount: custom === 'true' ? true : !!isDonationBox,
          thankYouPagePath: thankYouPagePath,
          amount: parseInt(amount),
          period: (frequency as 'oneTime' | 'monthly' | 'yearly') || 'oneTime',
          description,
          testMode: !process.env.GATSBY_RAISENOW_PRODUCTION,
          // @ts-ignore
          hasDisclaimer: !!input?.disclaimer,
        },
        200,
      ];
    },
  };
}

export function mapDonationPage(
  input:
    | ((
        | (DonationPageFragment | undefined)
        | (StockDonationPageFragment | undefined)
        | (SponsorshipDonationPageFragment | undefined)
        | (BoxDonationPageFragment | undefined)
      ) &
        WidgetIds)
    | undefined,
  pageContext: SilverbackPageContext,
): RouteInput<typeof DonationRoute> {
  const headerInput = {
    headline: input?.callToAction || '',
    lead: input?.lead || '',
  };
  const isBoxDonation = input?.__typename === 'BoxDonation';
  const donationBox = mapDonationBox(
    isBoxDonation ? input : undefined,
    pageContext,
  );
  const donationForm = mapDonationForm(
    !isBoxDonation ? input : undefined,
    pageContext,
  );

  const body: any = [
    ...[mapDonationPageHeader(headerInput)],
    ...(isBoxDonation ? [donationBox] : []),
    ...(!isBoxDonation ? [donationForm] : []),
    // @ts-ignore
    ...(input?.disclaimer ? [mapDonationPageDisclaimer(input)] : []),
  ];

  return {
    body,
  };
}
